import { useState, useEffect } from "react";
import { isSwimmingEvent } from "../utils/eventParser";

export function useSwimmingData() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        // Aggressive cache busting - timestamp + random string
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 15);

        // Use regular GitHub Pages URL with cache busting
        const dataUrl = `${process.env.PUBLIC_URL}/data/pool.json?_t=${timestamp}&_r=${random}`;

        console.log('Fetching pool data from:', dataUrl);

        const response = await fetch(dataUrl, {
          method: 'GET',
          cache: 'reload', // Most aggressive cache policy
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.status}`);
        }

        const jsonData = await response.json();

        // Log the timestamp to verify fresh data
        const fetchedTimestamp = new Date(jsonData.lastUpdated);
        console.log('Pool data timestamp:', fetchedTimestamp.toISOString());

        // Process the data
        const processedData = processData(jsonData.data);

        // Update state
        setData(processedData);
        setLastUpdated(fetchedTimestamp);
      } catch (err) {
        console.error("Error fetching swimming data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  return { data, loading, error, lastUpdated };
}

// Process swimming data
function processData(rawData) {
  if (!rawData || !Array.isArray(rawData)) return [];

  const processedEvents = rawData
    .filter(isSwimmingEvent)
    .map((event) => {

      // Skip events with invalid dates
      if (!event.start || !event.end) {
        return null;
      }

      return {
        id: event.id,
        title: event.title,
        start: event.start,
        end: event.end,
        backgroundColor: event.backgroundColor,
        textColor: event.textColor,
        allDay: event.allDay,
      };
    })
    .filter((event) => event !== null);

  // Deduplicate events with identical time slots (API returns duplicates for private events)
  const seenTimeSlots = new Set();
  const deduplicated = processedEvents.filter((event) => {
    const timeSlot = `${event.start}-${event.end}`;
    if (seenTimeSlots.has(timeSlot)) {
      return false;
    }
    seenTimeSlots.add(timeSlot);
    return true;
  });

  return deduplicated.sort((a, b) => a.start - b.start);
}
